import {format, eachDayOfInterval} from 'date-fns';

/** Minimal shape required for date grouping */
interface Dateable {
  startDate: Date | string | null;
  endDate: Date | string | null;
}

export interface EventDateGroup<T> {
  dateKey: string;
  date: Date | null;
  events: T[];
}

/**
 * Groups events by date key (yyyy-MM-dd) preserving chronological order.
 * Multi-day events appear under each day they span. Events without a
 * startDate are collected into a trailing "No date" group.
 */
export function groupEventsByDate<T extends Dateable>(events: T[]): EventDateGroup<T>[] {
  const groups: EventDateGroup<T>[] = [];
  const map = new Map<string, EventDateGroup<T>>();
  const undated: T[] = [];

  for (const event of events) {
    if (!event.startDate) {
      undated.push(event);
      continue;
    }
    const start = new Date(event.startDate);
    const end = event.endDate ? new Date(event.endDate) : start;
    const days = eachDayOfInterval({start, end});

    for (const d of days) {
      const key = format(d, 'yyyy-MM-dd');
      const existing = map.get(key);
      if (existing) {
        existing.events.push(event);
      } else {
        const group = {dateKey: key, date: d, events: [event]};
        groups.push(group);
        map.set(key, group);
      }
    }
  }

  groups.sort((a, b) => a.dateKey.localeCompare(b.dateKey));

  if (undated.length > 0) {
    groups.push({dateKey: 'no-date', date: null, events: undated});
  }

  return groups;
}
